// Toasts bestätigen, was geschehen ist (design-system §4.18). Fläche `inverse`, Desktop unten rechts (höchstens
// drei), Telefon oben. Umkehrbare Aktionen zeigen 8 Sekunden „Rückgängig“ mit ablaufender Linie.
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react'
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { cn } from '../lib/cn'
import { useHotkey } from '../lib/hotkeys'
import { LayerProvider, Z } from './context'
import { Spinner } from './Spinner'
import type { Tone } from './types'

export interface ToastOptions {
  /** gleiches id ersetzt statt zu stapeln */
  id?: string
  /** „Umlagerung UM-2026-0875 angelegt“ */
  title: string
  description?: string
  tone?: Tone
  /** „Öffnen“ */
  action?: { label: string; onSelect: () => void }
  /** zeigt „Rückgängig“ mit ablaufender Linie */
  undo?: () => void | Promise<void>
  /** ms; Standard 5000, mit undo 8000, bei 'danger' bleibt er stehen (0 = bleibt) */
  duration?: number
}

interface ToastEntry extends ToastOptions {
  id: string
  tone: Tone
  duration: number
  loading?: boolean
  createdAt: number
}

const MAX_VISIBLE = 3
let entries: readonly ToastEntry[] = []
let counter = 0
const listeners = new Set<() => void>()
const emit = (): void => listeners.forEach((listener) => listener())
const setEntries = (next: readonly ToastEntry[]): void => {
  entries = next
  emit()
}

function show(options: ToastOptions & { loading?: boolean }): string {
  const id = options.id ?? `toast-${++counter}`
  const tone = options.tone ?? 'neutral'
  const duration = options.duration ?? (options.loading || tone === 'danger' ? 0 : options.undo ? 8000 : 5000)
  const entry: ToastEntry = { ...options, id, tone, duration, createdAt: Date.now() }
  const existing = entries.some((item) => item.id === id)
  setEntries(existing ? entries.map((item) => (item.id === id ? entry : item)) : [...entries, entry])
  return id
}

function dismiss(id?: string): void {
  setEntries(id === undefined ? [] : entries.filter((item) => item.id !== id))
}

export const toast = {
  show: (options: ToastOptions): string => show(options),
  success: (title: string, options?: Omit<ToastOptions, 'title' | 'tone'>): string =>
    show({ ...options, title, tone: 'success' }),
  /** bleibt stehen, bis er geschlossen wird – und sagt, was nicht geschehen ist */
  error: (title: string, options?: Omit<ToastOptions, 'title' | 'tone'>): string =>
    show({ ...options, title, tone: 'danger' }),
  promise<T>(
    promise: Promise<T>,
    texts: { loading: string; success: (value: T) => string; error: (error: unknown) => string },
  ): Promise<T> {
    const id = show({ title: texts.loading, loading: true })
    promise.then(
      (value) => show({ id, title: texts.success(value), tone: 'success' }),
      (error: unknown) => show({ id, title: texts.error(error), tone: 'danger' }),
    )
    return promise
  },
  dismiss,
}

const ICONS = { info: Info, success: CircleCheck, warning: TriangleAlert, danger: CircleAlert } as const
// Auf der inversen Fläche gelten die Statusfarben des jeweils anderen Themes – deshalb feste, kontrastreiche Marken.
const ICON_TONE = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
} as const

function ToastItem({ entry }: { entry: ToastEntry }) {
  const [paused, setPaused] = useState(false)
  const [undoing, setUndoing] = useState(false)
  const remaining = useRef(entry.duration)
  const startedAt = useRef(0)

  useEffect(() => {
    remaining.current = entry.duration
  }, [entry.duration, entry.createdAt])

  useEffect(() => {
    if (entry.duration <= 0 || paused || undoing) return
    startedAt.current = Date.now()
    const timer = setTimeout(() => dismiss(entry.id), remaining.current)
    return () => {
      clearTimeout(timer)
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current))
    }
  }, [entry.id, entry.duration, entry.createdAt, paused, undoing])

  const runUndo = async (): Promise<void> => {
    if (!entry.undo || undoing) return
    setUndoing(true)
    try {
      await entry.undo()
      dismiss(entry.id)
    } catch {
      show({ id: entry.id, title: 'Rückgängig nicht möglich. Es wurde nichts geändert.', tone: 'danger' })
    } finally {
      setUndoing(false)
    }
  }

  const Icon = entry.tone === 'neutral' ? null : ICONS[entry.tone]
  const button =
    'hit-area shrink-0 rounded-xs px-1.5 py-0.5 text-dense font-semibold underline-offset-2 hover:underline'
  return (
    <li
      role={entry.tone === 'danger' ? 'alert' : 'status'}
      aria-live={entry.tone === 'danger' ? 'assertive' : 'polite'}
      data-ui="toast"
      data-tone={entry.tone}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="pointer-events-auto relative flex w-full animate-pop-in items-start gap-2.5 overflow-hidden rounded-md bg-inverse px-3.5 py-3 text-fg-inverse shadow-lg md:w-96"
    >
      {entry.loading ? (
        <Spinner className="mt-0.5 size-4" />
      ) : (
        Icon && (
          <Icon
            className={cn('mt-0.5 size-4 shrink-0', ICON_TONE[entry.tone as keyof typeof ICON_TONE])}
            strokeWidth={2}
            aria-hidden
          />
        )
      )}
      <div className="min-w-0 flex-1">
        <p className="text-dense font-medium">{entry.title}</p>
        {entry.description && <p className="mt-0.5 text-caption opacity-75">{entry.description}</p>}
      </div>
      {entry.undo && (
        <button type="button" className={button} onClick={() => void runUndo()} disabled={undoing}>
          {undoing ? 'Wird zurückgenommen …' : 'Rückgängig'}
        </button>
      )}
      {entry.action && (
        <button
          type="button"
          className={button}
          onClick={() => {
            entry.action?.onSelect()
            dismiss(entry.id)
          }}
        >
          {entry.action.label}
        </button>
      )}
      {!entry.loading && (
        <button
          type="button"
          aria-label="Meldung schließen"
          onClick={() => dismiss(entry.id)}
          className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-xs opacity-60 hit-area hover:opacity-100"
        >
          <X className="size-3.5" strokeWidth={2} aria-hidden />
        </button>
      )}
      {entry.undo && entry.duration > 0 && (
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-0.5 origin-left animate-countdown bg-fg-inverse/50"
          style={{
            animationDuration: `${entry.duration}ms`,
            animationPlayState: paused || undoing ? 'paused' : 'running',
          }}
        />
      )}
    </li>
  )
}

/** Einmal in der Anwendung einhängen (macht <UiProvider>). */
export function ToastViewport() {
  const items = useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => entries,
    () => entries,
  )
  const visible = items.slice(-MAX_VISIBLE)
  // mod+Z außerhalb eines Felds nimmt die letzte umkehrbare Aktion zurück, solange ihr Toast sichtbar ist (§6.3)
  const undoable = [...visible].reverse().find((item) => item.undo)
  useHotkey(
    'mod+z',
    () => {
      if (!undoable?.undo) return
      void Promise.resolve(undoable.undo()).then(
        () => dismiss(undoable.id),
        () => show({ id: undoable.id, title: 'Rückgängig nicht möglich. Es wurde nichts geändert.', tone: 'danger' }),
      )
    },
    { enabled: Boolean(undoable), allowInInputs: false },
  )
  return (
    <LayerProvider z={Z.toast}>
      <ol
        aria-label="Meldungen"
        data-ui="toast-viewport"
        data-print="hide"
        // Desktop: unten rechts – steht dort eine feste Aktionsleiste (data-ui="action-bar"), rückt der Bereich über
        // sie: Ein Hinweis verdeckt nie die Hauptaktion (Wunsch finanzen-02 §5). Telefon: oben, unter der Kopfzeile.
        className="pointer-events-none toast-top fixed inset-x-3 flex flex-col gap-2 md:inset-x-auto md:top-auto md:right-4 md:bottom-4 md:items-end md:[html:has([data-ui='action-bar'])_&]:bottom-[4.75rem]"
        style={{ zIndex: Z.toast }}
      >
        {visible.map((entry) => (
          <ToastItem key={entry.id} entry={entry} />
        ))}
      </ol>
    </LayerProvider>
  )
}

/** Nur für Tests: alle Meldungen entfernen. */
export const resetToasts = (): void => setEntries([])
